// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MdAdd, MdLocationOn, MdPriceChange, MdRefresh } from "react-icons/md";
import { BiWorld } from "react-icons/bi";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import SearchBar from "../UI/SearchBar";
import VehicleCard from "./VehicleCard";
import Heading from "../UI/Heading";
import FilterMenu from "../UI/FilterMenu";
import PaginationMenu from "../UI/PaginationMenu";
import DateFilter from "../UI/DateFilter";
import LoadingCard from "../UI/LoadingCard";
import ExportCsvButton from "../UI/ExportCsvButton";
import NoRecordsFound from "../UI/NoRecordsFound";
import Switch from "../UI/Switch";

export default function VehiclesPage() {
  const { navigate } = useContext(UtilityContext);
  const { locations } = useContext(DataContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filters
  const defaultFilters = {
    query: "",
    limit: 50,
    home: "",
    location: "",
    booking: "All",
    availability: "All",
    status: "",
    plan: "",
    tracker: "All",
    ntta: "All",
    body: "",
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [fetchLocations, setFetchLocations] = useState(false);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    // If location filter but locations are disabled, enable fetch locations and wait for useEffect to run again
    if (filter.location && !fetchLocations) {
      setFetchLocations(true);
      return;
    }

    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "vehicle",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/vehicles?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });
      setVehicles(data);

      if (fetchLocations) getLocations(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= FETCH LOCATIONS ======================= */
  const getLocations = async (vehicles) => {
    try {
      setLoading(true);
      if (vehicles?.length > 0) {
        let locations = [];
        try {
          // Fetch Locations
          locations = await requestAdminAPI("fetch-vehicle-locations", {
            vehicles: vehicles.map((v) => ({
              vin: v.vin,
              trackerId: v.trackerId,
            })),
          });
        } catch (err) {
          console.error(err);
        }

        const locationFiltered = [];
        const selectedLocations = filter.location.split(",");
        // Add Location to Vehicles
        vehicles.forEach((vehicle) => {
          const location = locations.find(
            (l) => l.vin === vehicle.vin || l.trackerId === vehicle.trackerId,
          );
          vehicle.fetched = true;
          vehicle.location = location?.location?.address;
          vehicle.lot = location?.location?.lot;
          if (filter.location) {
            if (vehicle.lot && selectedLocations.includes(vehicle.lot.replace(",", " ")))
              locationFiltered.push(vehicle);
            else if (selectedLocations.includes("Not On Lot") && !vehicle.lot)
              locationFiltered.push(vehicle);
            else if (selectedLocations.includes("Unknown") && !vehicle.location)
              locationFiltered.push(vehicle);
          } else locationFiltered.push(vehicle);
        });

        setVehicles(locationFiltered);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  // =================== FILTER HANDLER ===================
  const filterOptions = [
    {
      label: "Location",
      options: [
        ...locations.map((l) => ({
          label: l.name,
          value: l.name.replace(",", " "),
        })),
        "Not On Lot",
        "Unknown",
      ],
      selected: filter.location,
      type: "multiple",
    },
    {
      label: "Home",
      options: [...locations.map((l) => ({ label: l.name, value: l.id }))],
      selected: filter.home,
      type: "multiple",
    },
    {
      label: "Booking",
      options: ["All", "Booked", "Not Booked"],
      selected: filter.booking,
    },
    {
      label: "Availability",
      options: ["All", "Available", "Not Available"],
      selected: filter.availability,
    },
    {
      label: "Status",
      options: ["Available", "Maintenance", "Sold", "Paused", "Totalled"],
      selected: filter.status,
      type: "multiple",
    },
    {
      label: "Plan",
      options: ["LUXURY", "PLUSH", "POSH"],
      selected: filter.plan,
      type: "multiple",
    },
    {
      label: "Body",
      options: [
        { label: "Sedan", value: "sedan" },
        {
          label: "SUV",
          value: "suv",
        },
        { label: "Truck", value: "truck" },
        { label: "Coupe", value: "coupe" },
        { label: "Convertible", value: "convertible" },
      ],
      selected: filter.body,
      type: "multiple",
    },
    {
      label: "Tracker",
      options: ["All", "Installed", "Not Installed"],
      selected: filter.tracker,
    },
    {
      label: "NTTA",
      options: ["All", "Registered", "Not Registered"],
      selected: filter.ntta,
    },
  ];

  // Apply Filters
  useEffect(() => {
    fetchRecords();
  }, [filter]);

  // Fetch Locations
  useEffect(() => {
    if (fetchLocations) fetchRecords();
  }, [fetchLocations]);

  return (
    <div className="container grid">
      <Heading title="Vehicles" isBackArrow={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <Switch
          on={fetchLocations}
          onSwitch={() => setFetchLocations(!fetchLocations)}
          Icon={MdLocationOn}
          title="Fetch Locations"
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdAdd
          onClick={(e) => navigate("/vehicles/add", e)}
          className="text-3xl icon"
          title="Add Vehicle"
        />
        <MdPriceChange
          onClick={(e) => navigate("/vehicles/pricing", e)}
          className="icon"
          title="Update Pricing"
        />
        <BiWorld
          onClick={(e) => navigate("/brands", e)}
          className="icon"
          title="Add Brand"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="icon"
          title="Refresh Data"
        />
        <ExportCsvButton array={vehicles} filename="vehicles" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={8} />
      ) : vehicles.length > 0 ? (
        <div className="list">
          {vehicles.map((vehicle) => (
            <VehicleCard
              vehicle={vehicle}
              key={vehicle.id}
              fetchLocations={fetchLocations}
            />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        limit={filter.limit}
        setLimit={(lim) => setFilter({ ...filter, limit: lim })}
        loading={loading}
        count={vehicles.length}
      />
    </div>
  );
}
